

const STORAGE_KEY = 'cansativa_cart';

const load = () => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    const parsed = raw ? JSON.parse(raw) : [];
    if (!Array.isArray(parsed)) return [];
    // Carts saved before articles carried an inventory id fall back to the PZN.
    parsed.forEach((line) => line?.articles?.forEach((a) => { if (!a.id) a.id = a.pzn; }));
    return parsed;
  } catch {
    return [];
  }
};

const persist = (lines) => {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(lines));
  } catch {
    // Storage full or unavailable — the cart still works for this session.
  }
};

// Articles are keyed by inventory row id, NOT by PZN: one PZN can appear as several
// batches (same packaging, different expiry and price), and each must toggle on its own.
const findArticle = (state, productId, articleId) =>
  state.lines.find((l) => l.productId === productId)?.articles.find((a) => a.id === articleId);

const state = {
  lines: load()
};

const mutations = {
  addLine(state, line) {
    const existing = state.lines.find((l) => l.productId === line.productId);
    if (existing) {
      // Re-added from the table with a fresh amount: take the new package counts and
      // selection, so the cart reflects what the Bestellung field said on this click.
      for (const article of line.articles) {
        const known = existing.articles.find((a) => a.id === article.id);
        if (known) {
          known.packages = article.packages;
          known.selected = article.selected;
        } else {
          existing.articles.push(article);
        }
      }
      existing.selected = true;
    } else {
      state.lines.push(line);
    }
    persist(state.lines);
  },
  removeLine(state, productId) {
    state.lines = state.lines.filter((l) => l.productId !== productId);
    persist(state.lines);
  },
  removeArticle(state, { productId, articleId }) {
    const line = state.lines.find((l) => l.productId === productId);
    if (!line) return;
    line.articles = line.articles.filter((a) => a.id !== articleId);
    // A product with no articles left has nothing to order.
    if (!line.articles.length) {
      state.lines = state.lines.filter((l) => l.productId !== productId);
    }
    persist(state.lines);
  },
  setLineSelected(state, { productId, selected }) {
    const line = state.lines.find((l) => l.productId === productId);
    if (!line) return;
    line.selected = selected;
    line.articles.forEach((a) => { a.selected = selected; });
    persist(state.lines);
  },
  setArticleSelected(state, { productId, articleId, selected }) {
    const article = findArticle(state, productId, articleId);
    if (!article) return;
    article.selected = selected;
    const line = state.lines.find((l) => l.productId === productId);
    line.selected = line.articles.some((a) => a.selected);
    persist(state.lines);
  },
  setArticlePackages(state, { productId, articleId, packages }) {
    const article = findArticle(state, productId, articleId);
    if (!article) return;
    // Cansativa rejects quantity < 1, so the cart never holds less.
    const parsed = Math.trunc(Number(packages));
    article.packages = Number.isFinite(parsed) && parsed >= 1 ? parsed : 1;
    persist(state.lines);
  },
  // Package size Cansativa quotes for that PZN. A line saved while Cansativa gave
  // no quote holds 0, so the size is filled in as soon as one arrives.
  setArticlePackaging(state, { productId, pzn, packaging }) {
    const line = state.lines.find((l) => l.productId === productId);
    const size = Number(packaging) || 0;
    if (!line || !size) return;

    // Write-once: tier_applied grows with the quantity, so a later quote would
    // redefine the package and multiply the amount.
    for (const article of line.articles.filter((a) => a.pzn === pzn && !a.packaging)) {
      article.packaging = size;
    }
    persist(state.lines);
  },
  // Cansativa's own tier price, fetched on the checkout page. `unit_price` is cents
  // per package; we store EUR per gram so the existing price maths keeps working.
  // `unit_price` means two different things depending on the product: for extracts
  // it is already the price per ml, for buds it is the price of a whole package.
  // Verified against /v1/pricing, which returns the per-gram price outright.
  setArticlePricing(state, { productId, pzn, unitPriceCents, tier, source }) {
    const line = state.lines.find((l) => l.productId === productId);
    if (!line) return;

    const perUnit = unitPriceCents / 100;
    const isExtract = line.type === 'extracts';

    // Cansativa prices per PZN, so every batch sharing that PZN gets the same price.
    for (const article of line.articles.filter((a) => a.pzn === pzn && a.packaging)) {
      article.pricePerUnit = isExtract ? perUnit : perUnit / article.packaging;
      article.tier = tier ?? null;
      article.priceSource = source ?? null;
    }
    persist(state.lines);
  },
  // MHD of the current batch, fetched on the checkout page. Cansativa keeps one
  // current batch per PZN, so every article with that PZN gets the same date.
  setArticleMhd(state, { pzn, mhd }) {
    for (const line of state.lines) {
      for (const article of line.articles.filter((a) => a.pzn === pzn)) {
        article.mhd = mhd ?? null;
      }
    }
    persist(state.lines);
  },
  clear(state) {
    state.lines = [];
    persist(state.lines);
  }
};

const actions = {
  add: ({ commit }, line) => commit('addLine', line),
  removeLine: ({ commit }, productId) => commit('removeLine', productId),
  removeArticle: ({ commit }, payload) => commit('removeArticle', payload),
  selectLine: ({ commit }, payload) => commit('setLineSelected', payload),
  selectArticle: ({ commit }, payload) => commit('setArticleSelected', payload),
  setPackages: ({ commit }, payload) => commit('setArticlePackages', payload),
  setPackaging: ({ commit }, payload) => commit('setArticlePackaging', payload),
  setPricing: ({ commit }, payload) => commit('setArticlePricing', payload),
  setMhd: ({ commit }, payload) => commit('setArticleMhd', payload),
  clear: ({ commit }) => commit('clear')
};

// Grams = packages x packaging size; price = grams x price per gram. Both display-only.
export const articleGrams = (article) =>
  (Number(article.packages) || 0) * (Number(article.packaging) || 0);

export const articlePrice = (article) =>
  articleGrams(article) * (Number(article.pricePerUnit) || 0);

const getters = {
  lines: (state) => state.lines,
  count: (state) => state.lines.length,
  hasProduct: (state) => (productId) => state.lines.some((l) => l.productId === productId),

  requestedAmount: (state) => (productId) => {
    const first = state.lines.find((l) => l.productId === productId)?.articles?.[0];
    return first ? articleGrams(first) : null;
  },
  lineTotal: () => (line) =>
    line.articles.filter((a) => a.selected).reduce((sum, a) => sum + articlePrice(a), 0),
  grandTotal: (state) =>
    state.lines
      .filter((l) => l.selected)
      .reduce(
        (sum, l) => sum + l.articles.filter((a) => a.selected).reduce((s, a) => s + articlePrice(a), 0),
        0
      ),
  // What goes to the Ordering API: PZN and package count only, no prices.
  orderItems: (state) =>
    state.lines
      .filter((l) => l.selected)
      .flatMap((l) => l.articles.filter((a) => a.selected))
      .map((a) => ({ pzn: a.pzn, quantity: Number(a.packages) || 1 })),

  emailItems: (state) =>
    state.lines
      .filter((l) => l.selected)
      .flatMap((line) =>
        line.articles
          .filter((a) => a.selected)
          .map((a) => ({
            pzn: a.pzn,
            quantity: Number(a.packages) || 1,
            productId: line.productId,
            productName: line.name,
            cultivar: line.cultivar,
            amount: articleGrams(a),
            unit: line.unit,
            packages: Number(a.packages) || 1,
            mhd: a.mhd ?? null,
            price: Math.round(articlePrice(a) * 100) / 100
          }))
      )
};

export default {
  namespaced: true,
  state,
  mutations,
  actions,
  getters
};
